import { useState, useRef, useMemo } from "react";
import { X, Upload, Trash2, Check, Image, Folder } from "lucide-react";
import { useAdmin } from "./admin-context";
import { apiUrl } from "../../utils/config";

const BASE_URL = apiUrl?.replace("/api", "") || "";

/**
 * MediaPicker
 * Single mode (default):
 *   onSelect({ id, url })  — called immediately on click
 *   selectedId             — currently selected media id
 *
 * Multi-select mode:
 *   multiple={true}
 *   onSelectMultiple([{ id, url }])  — called when "Done" is clicked
 *   selectedIds={[]}                 — already-selected media ids
 */
const MediaPicker = ({ onSelect, onClose, selectedId, multiple = false, onSelectMultiple, selectedIds = [] }) => {
  const { mediaFiles, uploadMedia, deleteMedia } = useAdmin();
  const [activeFolder, setActiveFolder] = useState(null); // null = all
  const [uploading, setUploading]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [multiSelected, setMultiSelected] = useState(() => new Set(selectedIds));
  const fileInputRef = useRef();

  const folders = useMemo(() => {
    const set = new Set(mediaFiles.map((f) => f.folder).filter(Boolean));
    return Array.from(set).sort();
  }, [mediaFiles]);

  const visibleFiles = useMemo(
    () => activeFolder ? mediaFiles.filter((f) => f.folder === activeFolder) : mediaFiles,
    [mediaFiles, activeFolder]
  );

  const handleFiles = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      await uploadMedia(fd, activeFolder || "general");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Image size={18} className="text-orange-500" />
            <h2 className="text-base font-bold text-gray-800">Media Library</h2>
            <span className="text-xs text-gray-400">({visibleFiles.length} files)</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg text-sm font-bold transition disabled:opacity-60"
            >
              <Upload size={14} />
              {uploading ? "Uploading…" : "Upload"}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
            {multiple && (
              <span className="text-xs text-gray-500 font-medium">{multiSelected.size} selected</span>
            )}
            <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-full transition">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Folder tabs */}
        {folders.length > 0 && (
          <div className="flex items-center gap-1 px-4 pt-3 pb-0 overflow-x-auto flex-shrink-0">
            <button
              onClick={() => setActiveFolder(null)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition
                ${!activeFolder ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              <Image size={11} /> All
              <span className={`ml-0.5 ${!activeFolder ? "text-orange-200" : "text-gray-400"}`}>({mediaFiles.length})</span>
            </button>
            {folders.map((f) => {
              const cnt = mediaFiles.filter((m) => m.folder === f).length;
              return (
                <button
                  key={f}
                  onClick={() => setActiveFolder(f)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition
                    ${activeFolder === f ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  <Folder size={11} /> {f}
                  <span className={`ml-0.5 ${activeFolder === f ? "text-orange-200" : "text-gray-400"}`}>({cnt})</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4 pb-2">
          {visibleFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-3">
              <Image size={40} className="opacity-30" />
              <p className="text-sm">{activeFolder ? `No images in "${activeFolder}".` : "No images yet."}</p>
              <button type="button" onClick={() => fileInputRef.current?.click()} className="text-orange-500 text-sm font-bold underline">
                Upload now
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {visibleFiles.map((file) => {
                const fullUrl = `${BASE_URL}${file.url}`;
                const isSelected = multiple ? multiSelected.has(file.id) : selectedId === file.id;
                const handleClick = () => {
                  if (multiple) {
                    setMultiSelected(prev => {
                      const next = new Set(prev);
                      next.has(file.id) ? next.delete(file.id) : next.add(file.id);
                      return next;
                    });
                  } else {
                    onSelect({ id: file.id, url: fullUrl });
                  }
                };
                return (
                  <div
                    key={file.id}
                    className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all
                      ${isSelected ? "border-orange-500 ring-2 ring-orange-300" : "border-transparent hover:border-orange-300"}`}
                  >
                    <div className="aspect-square bg-gray-100" onClick={handleClick}>
                      <img src={fullUrl} alt={file.name} className="w-full h-full object-cover" />
                    </div>

                    {/* ID badge */}
                    <div className="absolute top-1 left-1 flex gap-0.5">
                      <span className="bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded font-mono">#{file.id}</span>
                    </div>

                    {isSelected && (
                      <div className="absolute top-1 right-1 bg-orange-500 rounded-full p-0.5">
                        <Check size={10} className="text-white" />
                      </div>
                    )}

                    {confirmDelete === file.id ? (
                      <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-1 p-1">
                        <p className="text-white text-[9px] text-center font-bold">Delete?</p>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => { deleteMedia(file.id); setConfirmDelete(null); }}
                            className="bg-red-500 text-white text-[9px] px-2 py-0.5 rounded font-bold">Yes</button>
                          <button type="button" onClick={() => setConfirmDelete(null)}
                            className="bg-gray-400 text-white text-[9px] px-2 py-0.5 rounded font-bold">No</button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(file.id); }}
                        className="absolute bottom-1 right-1 bg-black/50 hover:bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                      >
                        <Trash2 size={10} />
                      </button>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition">
                      {file.name}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      {/* Multi-select footer */}
      {multiple && (
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50 flex-shrink-0">
          <span className="text-sm text-gray-500">
            {multiSelected.size > 0 ? `${multiSelected.size} image${multiSelected.size !== 1 ? 's' : ''} selected` : 'Click images to select'}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-100 transition">
              Cancel
            </button>
            <button
              type="button"
              disabled={multiSelected.size === 0}
              onClick={() => {
                const items = visibleFiles
                  .filter(f => multiSelected.has(f.id))
                  .map(f => ({ id: f.id, url: `${BASE_URL}${f.url}` }));
                onSelectMultiple(items);
              }}
              className="px-4 py-2 text-sm font-bold bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Add {multiSelected.size > 0 ? multiSelected.size : ''} Image{multiSelected.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MediaPicker;
